/**
 * LeadOutreachChip — "On outreach list: <name> · try 2 of 3 · Sarah" on the lead header, with the
 * outcome control beside it (docs/crm-outreach-lists-plan.md §B6/§B7). Wherever the recruiter is
 * when the call ends — the bar, Call Mode, or here on the lead page — they can log how it went.
 *
 * FETCHED ONLY FOR LIST LEADS. GET /contacts/:id selects *, so the contact payload already carries
 * `active_outreach_list_id`; the chip asks GET /contacts/:id/outreach-list only when that is set.
 * Every other lead page costs nothing.
 *
 * `useLeadOutreach` is the one read of that endpoint on a lead page. The Callbacks card needs the
 * same member (its cadence line becomes the list's "try 2 of 3 · next try …"), and two cards
 * asking separately would be two requests per page load and two answers that can disagree after
 * an outcome. So the read is a tiny module store keyed by contact + list: concurrent mounts share
 * one in-flight request, and a refresh from either card repaints both. It refreshes on the CRM's
 * stats-changed event too, which is what an outcome logged from the Next-up bar dispatches.
 *
 * Any failure hides the chip (a 404 is an api without outreach lists — plan §8): the chip is a
 * convenience on top of the page, never a reason to show an error on it.
 */
import { useCallback, useEffect, useReducer } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { STATS_CHANGED_EVENT } from '../lib';
import { getLeadOutreach } from './api';
import OutcomeControl from './OutcomeControl';
import { FLAG_LABELS, SERVABLE_BUCKETS, bucketLabel } from './outreachLogic';
import { leadChipDetail } from './outreachSurfaces';
import type { LeadOutreachInfo } from './types';

type LeadMember = LeadOutreachInfo['member'];

/** A refresh within this window reuses the last answer (a remount is not new information). */
const FRESH_MS = 10_000;

const snapshots = new Map<string, { member: LeadMember; at: number }>();
const inflight = new Map<string, Promise<void>>();
const subscribers = new Map<string, Set<() => void>>();

function loadLeadOutreach(key: string, contactId: string): Promise<void> {
  const running = inflight.get(key);
  if (running) return running;
  const p = getLeadOutreach(contactId)
    .then((info) => { snapshots.set(key, { member: info.member, at: Date.now() }); })
    .catch(() => {
      // Keep a member we already showed through a transient failure; with nothing shown yet,
      // record "not on a list" so the chip stays hidden instead of retrying in a loop.
      const prev = snapshots.get(key);
      snapshots.set(key, { member: prev?.member ?? null, at: Date.now() });
    })
    .finally(() => {
      inflight.delete(key);
      subscribers.get(key)?.forEach((fn) => fn());
    });
  inflight.set(key, p);
  return p;
}

/**
 * The lead's open outreach-list member, or null. `activeListId` is the contact payload's
 * `active_outreach_list_id`; while it is empty nothing is fetched and the answer is null.
 */
export function useLeadOutreach(
  contactId: string | null | undefined,
  activeListId: string | null | undefined,
): { member: LeadMember; refresh: () => void } {
  const key = contactId && activeListId ? `${contactId}:${activeListId}` : null;
  const [, repaint] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!key || !contactId) return undefined;
    const subs = subscribers.get(key) ?? new Set<() => void>();
    subs.add(repaint);
    subscribers.set(key, subs);
    const snap = snapshots.get(key);
    if (!snap || Date.now() - snap.at > FRESH_MS) loadLeadOutreach(key, contactId);
    const onStatsChanged = () => { loadLeadOutreach(key, contactId); };
    window.addEventListener(STATS_CHANGED_EVENT, onStatsChanged);
    return () => {
      subs.delete(repaint);
      window.removeEventListener(STATS_CHANGED_EVENT, onStatsChanged);
    };
  }, [key, contactId]);

  const refresh = useCallback(() => {
    if (key && contactId) loadLeadOutreach(key, contactId);
  }, [key, contactId]);

  return { member: key ? snapshots.get(key)?.member ?? null : null, refresh };
}

export default function LeadOutreachChip({
  contactId,
  activeListId,
}: {
  contactId: string;
  /** The contact payload's `active_outreach_list_id`. */
  activeListId: string | null | undefined;
}) {
  const { member, refresh } = useLeadOutreach(contactId, activeListId);
  if (!member) return null;

  // Waiting states say when ("Next try Tue 10:00"); servable ones are already said by the try
  // count, and "Not called yet" is in the detail line — so only the waiting label is added.
  const waiting = !SERVABLE_BUCKETS.has(member.bucket) ? bucketLabel(member.bucket, member) : null;
  const flags = member.flags.slice(0, 2);

  return (
    // Phones: the sentence owns its own full line (basis-full) and the outcome
    // control drops below it at full width, instead of the two sharing one
    // row where the sentence was squeezed into a four-word column beside the
    // button. From sm up the icon, sentence and control share the row as before.
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 rounded-lg border border-primary-200 bg-primary-50/60 px-2.5 py-1.5 text-xs dark:border-primary-900/60 dark:bg-primary-900/20 sm:gap-y-1.5">
      <span className="flex min-w-0 flex-1 items-center gap-2 text-gray-700 dark:text-gray-200 max-sm:basis-full">
        <ListChecks className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          On outreach list:{' '}
          <Link
            to={`/crm/outreach/${member.list_id}`}
            title={`Open ${member.list_name}${member.study_title ? ` — ${member.study_title}` : ''}`}
            className="font-semibold text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300"
          >
            {member.list_name}
          </Link>
          <span className="text-gray-500 dark:text-gray-400"> · {leadChipDetail(member)}</span>
          {waiting && <span className="text-gray-500 dark:text-gray-400"> · {waiting}</span>}
          {flags.map((f) => (
            <span
              key={f}
              className="ml-1.5 hidden rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300 sm:inline-flex"
            >
              {FLAG_LABELS[f]}
            </span>
          ))}
        </span>
      </span>
      {member.can_log && (
        <OutcomeControl
          listId={member.list_id}
          memberId={member.id}
          contactId={contactId}
          surface="lead"
          onLogged={() => refresh()}
          onStale={refresh}
        />
      )}
    </div>
  );
}
